import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Address } from '@pdqdev/pdq-shared/dist/entities';
import {
  CountryCode,
  countryCodeToDisplayNameMap,
  CountryConversionNames,
  getCountryCodeByCountryName,
  getCountryDisplayNameByCountryCode,
} from '@pdqdev/pdq-shared/dist/utils/Country-utils';
import { Subscription } from 'rxjs';
import { debounceTime, finalize, skip, take } from 'rxjs/operators';
import { TimeUnits } from '../../../../../utils';
import { Shop } from '../../../../services/shops/shop.model';
import { ShopService } from '../../../../services/shops/shop.service';
import { FormattedAutoComplete } from '../../../../sharedModule/components/address-autocomplete/address-autocomplete.component';
import { DropdownOption } from '../../../../storybook/components/dropdown/dropdown.component';
import { PdqToastComponent } from '../../../../storybook/components/toast/toast.component';
import { ToastService } from '../../../../storybook/services/toast/toast.service';

export enum LocaitonType {
  POST_RETURN = 'returns',
  PRIMARY = 'primary',
}

enum PostSubmitError {
  ADDRESS_ERROR = 'Address requires correction',
}
class CountryOption extends DropdownOption {}
@Component({
  selector: 'location-edit',
  templateUrl: './location-edit.component.html',
  styleUrls: ['./location-edit.component.scss'],
  standalone: false,
})
export class LocationEditComponent implements OnInit, OnDestroy {
  isEditable: boolean = false;
  constructor(
    private shopService: ShopService,
    private route: ActivatedRoute,
    private router: Router,
    private toastService: ToastService,
  ) {
    this.isEditable = this.router.getCurrentNavigation()?.extras.state?.editable;
  }

  ngOnDestroy(): void {
    if (this.shopSubscription) {
      this.shopSubscription.unsubscribe();
    }
  }

  locationForm: UntypedFormGroup;
  pageTitle: string;
  countryOptions = Object.values(countryCodeToDisplayNameMap).map((contry) => {
    const res = new CountryOption();
    res.label = contry;
    res.value = contry;
    return res;
  });
  isValidAddress: boolean = true;
  showDangerBanner: boolean = false;
  postSubmitErrors: string[] = [];
  cityAutoCompleteRef: ElementRef;
  address: Address;
  autoCompleteMode: CountryCode;
  intAutoCompleteRef: ElementRef;
  countryDefault: CountryOption;
  shopSubscription: Subscription;
  isSaving: boolean = false;
  defaultCountry: string = CountryConversionNames.UNITED_STATES;
  ngOnInit(): void {
    this.shopSubscription = this.shopService.shop$.subscribe((shop) => {
      if (!shop) {
        return;
      }

      const address = this.findEditingAddress(shop);
      if (shop.address?.country)
        this.defaultCountry =
          shop.address.country.toUpperCase() in CountryCode
            ? getCountryDisplayNameByCountryCode(shop.address.country)
            : shop.address.country;
      this.address = address;
      this.initForm(address);
      this.initFormSubscriptions();
    });
  }

  onAutoCompleteSelected(values: FormattedAutoComplete) {
    this.locationForm.patchValue({
      address1: values.street,
      address2: values.street2,
      city: values.city,
      province: values.state ?? values.state_abbreviation,
      zip: values.zip,
    });
  }

  initCityAutoComplete(ref: ElementRef) {
    this.cityAutoCompleteRef = ref;
  }

  setCity(city: string) {
    this.locationForm.controls.city.setValue(city);
  }
  initIntAutoComplete(ref: ElementRef) {
    this.intAutoCompleteRef = ref;
  }
  findEditingAddress(shop: Shop) {
    const id = this.route.snapshot.paramMap.get('id');
    if (LocaitonType.PRIMARY === id) {
      this.pageTitle = 'Primary address';
      return shop.address;
    } else if (LocaitonType.POST_RETURN === id) {
      this.pageTitle = 'Return address';
      return shop.postOfficeReturn;
    } else {
      this.pageTitle = 'Edit location';
      return shop.shopLocations.find((location) => location.id === +id);
    }
  }
  handleCountryChange(country: CountryOption) {
    this.isValidAddress = true;
    this.locationForm.controls.country.setValue(country.value);
    this.autoCompleteMode = getCountryCodeByCountryName(country.value as string);
  }
  backToLocations() {
    this.router.navigate(['settings', 'locations']);
  }

  initFormSubscriptions() {
    this.locationForm.controls.address1.valueChanges.pipe(debounceTime(500)).subscribe((value) => {
      this.isValidAddress = true;
    });
    this.locationForm.controls.address2.valueChanges.pipe(skip(1)).subscribe(() => {
      this.locationForm.controls.adress2.markAsDirty();
      this.isValidAddress = true;
    });

    this.locationForm.controls.city.valueChanges.pipe(skip(1)).subscribe(() => {
      this.locationForm.controls.city.markAsDirty();
      this.isValidAddress = true;
    });
    this.locationForm.controls.province.valueChanges.pipe(skip(1)).subscribe(() => {
      this.locationForm.controls.province.markAsDirty();
      this.isValidAddress = true;
    });
    this.locationForm.controls.zip.valueChanges.pipe(skip(1)).subscribe(() => {
      this.locationForm.controls.zip.markAsDirty();
      this.isValidAddress = true;
    });
  }
  saveAddress() {
    let resAddress: Address;
    resAddress = Object.assign({}, this.address);
    resAddress.address1 = this.locationForm.value.address1;
    resAddress.address2 = this.locationForm.value.address2;
    resAddress.city = this.locationForm.value.city;
    resAddress.country = this.locationForm.value.country;
    resAddress.province = this.locationForm.value.province;
    resAddress.zip = this.locationForm.value.zip;
    resAddress.storeName = this.locationForm.value.locationName;
    resAddress.phone = this.locationForm.value.phone;
    this.isSaving = true;
    this.shopService
      .updatePostReturnAddress(resAddress)
      .pipe(
        take(1),
        finalize(() => (this.isSaving = false)),
      )
      .subscribe(
        (validation) => {
          if (validation.isValid) {
            this.isValidAddress = true;
            this.showDangerBanner = false;
            this.toastService.show(PdqToastComponent, {
              mode: 'success',
              message: 'Address updated',
              duration: TimeUnits._SECOND_IN_MS * 3,
            });
            this.processValidationErrors(validation);
            this.backToLocations();
          } else {
            this.showDangerBanner = true;
            this.processValidationErrors(validation);
            this.isValidAddress = false;
            this.toastService.show(PdqToastComponent, {
              mode: 'danger',
              message: 'Address verification failed',
              duration: TimeUnits._SECOND_IN_MS * 3,
            });
          }
        },
        () => {
          this.backToLocations();
        },
      );

    this.locationForm.markAsPristine();
  }

  processValidationErrors(validationResult) {
    if (validationResult.isValid) {
      this.postSubmitErrors = [];
    } else {
      if (validationResult.errors && validationResult.errors.length) {
        this.postSubmitErrors = validationResult.errors.map((error) => error.message);
      } else {
        this.postSubmitErrors = [PostSubmitError.ADDRESS_ERROR];
      }
    }
  }

  initForm(address: Address) {
    let country = this.defaultCountry;
    if (address?.country)
      country =
        address.country.toUpperCase() in CountryCode
          ? getCountryDisplayNameByCountryCode(address.country)
          : address.country;
    this.locationForm = new UntypedFormGroup({
      locationName: new UntypedFormControl({ value: address?.storeName ?? '', disabled: false }),
      address1: new UntypedFormControl({ value: address?.address1 ?? '', disabled: false }),
      address2: new UntypedFormControl({ value: address?.address2 ?? '', disabled: false }),
      city: new UntypedFormControl({ value: address?.city ?? '', disabled: false }),
      country: new UntypedFormControl({
        value: country,
        disabled: false,
      }),
      province: new UntypedFormControl({ value: address?.province ?? '', disabled: false }),
      zip: new UntypedFormControl({ value: address?.zip ?? '', disabled: false }),
      phone: new UntypedFormControl({ value: address?.phone ?? '', disabled: false }),
    });
    this.countryDefault = new CountryOption();

    this.countryDefault.label = country;
    this.countryDefault.value = country;
    this.autoCompleteMode = getCountryCodeByCountryName(country);

    if (!this.isEditable) {
      this.locationForm.disable();
    }
  }
}
